export interface ChatLeadData {
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  source?: string;
  notes?: string | null;
  language?: string;
  skipEmail?: boolean;
  forceEmail?: boolean;
  companyAddress?: string | null;
  companyCity?: string | null;
  companyZip?: string | null;
  website?: string | null;
  createAppointment?: boolean;
  appointmentDetails?: {
    date: string;
    title?: string;
    notes?: string;
  };
}

/**
 * Saves a chat lead to Supabase contacts table via server endpoint
 * Uses server-side endpoint to bypass RLS policies
 * 
 * This function is designed to be non-blocking and fail gracefully.
 * The chat flow should continue even if saving fails.
 */
export async function saveChatLead(data: ChatLeadData): Promise<{ success: boolean; error?: string; retryable?: boolean; emailSent?: boolean }> {
  try {
    // Validate required fields
    if (!data.email && !data.phone) {
      return { success: false, error: 'Email or phone required', retryable: false };
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      // Call server endpoint that uses service role key (bypasses RLS)
      const response = await fetch('/api/chat-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: data.full_name || null,
          email: data.email ? data.email.toLowerCase().trim() : null,
          phone: data.phone || null,
          company: data.company || null,
          source: data.source || 'chat-lead',
          language: data.language || 'it',
          skipEmail: data.skipEmail ?? false,
          forceEmail: data.forceEmail ?? false,
          companyAddress: data.companyAddress || null,
          companyCity: data.companyCity || null,
          companyZip: data.companyZip || null,
          website: data.website || null,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        // Try to parse error message, but don't fail if it's not JSON
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        // 5xx errors are retryable, 4xx are not
        const retryable = response.status >= 500;
        return { success: false, error: errorMessage, retryable };
      }

      // Parse JSON response
      let result;
      try {
        const text = await response.text();
        if (!text) {
          // Empty response, assume success
          return { success: true };
        }
        result = JSON.parse(text);
      } catch (parseError) {
        // Response is not valid JSON, but status was OK
        console.warn('[saveChatLead] Response is not JSON, but status is OK:', parseError);
        return { success: true }; // Assume success if status is OK
      }

      if (result.success === false) {
        return { success: false, error: result.message || 'Failed to save contact', retryable: true, emailSent: result.emailSent };
      }

      return { success: true, emailSent: result.emailSent };
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      
      // Handle different types of errors
      if (fetchError.name === 'AbortError') {
        return { success: false, error: 'Request timeout - server did not respond', retryable: true };
      }
      
      if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
        // Network error (server not running, CORS, etc.)
        return { success: false, error: 'Server unavailable - contact will be saved when server is online', retryable: true };
      }
      
      // Other errors
      return { success: false, error: fetchError?.message || 'Network error', retryable: true };
    }
  } catch (error: any) {
    // Catch-all for any unexpected errors
    console.error('[saveChatLead] Unexpected error:', error);
    return { success: false, error: error?.message || 'Unknown error', retryable: true };
  }
}

/**
 * Extracts contact information from chat messages
 */
export function extractContactInfo(messages: { role: string; content: string }[]): ChatLeadData {
  const allText = messages.map(m => m.content).join(' ').toLowerCase();
  
  // Email regex
  const emailMatch = allText.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/);
  
  // Phone regex (Italian format)
  // Relaxed regex to catch more formats, but still try to be specific enough
  const phoneMatch = allText.match(/(?:\+39|0039)?[\s.-]?[30][0-9]{2}[\s.-]?[0-9]{3}[\s.-]?[0-9]{3,4}/);
  
  // Name extraction (look for patterns like "mi chiamo", "sono", "il mio nome è")
  let name: string | null = null;
  const namePatterns = [
    /(?:mi chiamo|sono|il mio nome è|mi chiamo|chiamami)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
    /nome[:\s]+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
  ];
  for (const pattern of namePatterns) {
    const match = allText.match(pattern);
    if (match && match[1]) {
      name = match[1].trim();
      break;
    }
  }
  
  // Company extraction
  let company: string | null = null;
  const companyPatterns = [
    /(?:azienda|company|società|ditta)[:\s]+([A-Z][A-Za-z0-9\s&]+)/i,
    /(?:lavoro per|work for)[:\s]+([A-Z][A-Za-z0-9\s&]+)/i,
  ];
  for (const pattern of companyPatterns) {
    const match = allText.match(pattern);
    if (match && match[1]) {
      company = match[1].trim();
      break;
    }
  }

  return {
    full_name: name || null,
    email: emailMatch ? emailMatch[0] : null,
    phone: phoneMatch ? phoneMatch[0].replace(/\s/g, '') : null,
    company: company || null,
    source: 'cortex-analyst',
  };
}
